/**
 * Toast shim — wraps sonner so mobile only sees errors.
 *
 * Rule (see mem://platform/mobile-toasts-errors-only-v1):
 *   - Mobile (<768px): success/info/warning/loading/message/custom → no-op.
 *     toast.error and toast.offline still fire. toast.promise runs the
 *     promise but skips UI.
 *   - Desktop (≥768px): every method passes through to real sonner.
 *   - toast.forceSuccess always fires on both mobile and desktop, reserved
 *     for high-signal actions where the surface closes immediately (e.g.,
 *     "Copied to clipboard" from a dropdown).
 *
 * All 142 src files import `toast` from this module instead of 'sonner'
 * directly, so the rule applies app-wide with zero call-site edits.
 */
import { toast as sonnerToast, type ExternalToast } from 'sonner';

export { Toaster } from 'sonner';
export type { ExternalToast } from 'sonner';

const DUMMY_ID = -1;

function isMobile(): boolean {
	if (typeof window === 'undefined') return false;
	try {
		return window.matchMedia('(max-width: 767px)').matches;
	} catch {
		return false;
	}
}

type ToastFn = typeof sonnerToast;
type ToastArgs = Parameters<ToastFn>;

function noop(..._args: unknown[]): string | number {
	return DUMMY_ID;
}

// Build a wrapped callable that mirrors sonner's `toast` surface.
const wrapped = ((...args: ToastArgs) => {
	if (isMobile()) return DUMMY_ID;
	return sonnerToast(...args);
}) as ToastFn & {
	offline: (message?: string, data?: ExternalToast) => string | number;
	forceSuccess: typeof sonnerToast.success;
};

// Errors + dismiss always pass through.
wrapped.error = ((...args: Parameters<typeof sonnerToast.error>) =>
	sonnerToast.error(...args)) as typeof sonnerToast.error;
wrapped.dismiss = ((...args: Parameters<typeof sonnerToast.dismiss>) =>
	sonnerToast.dismiss(...args)) as typeof sonnerToast.dismiss;

// Force-fired success toast: bypasses mobile suppression for high-signal
// cross-surface actions (e.g., "Copied to clipboard") where the surface
// closes immediately and inline feedback is not visible.
wrapped.forceSuccess = ((...args: Parameters<typeof sonnerToast.success>) =>
	sonnerToast.success(...args)) as typeof sonnerToast.success;

// Silenced-on-mobile variants.
wrapped.success = ((...args: Parameters<typeof sonnerToast.success>) =>
	isMobile() ? DUMMY_ID : sonnerToast.success(...args)) as typeof sonnerToast.success;
wrapped.info = ((...args: Parameters<typeof sonnerToast.info>) =>
	isMobile() ? DUMMY_ID : sonnerToast.info(...args)) as typeof sonnerToast.info;
wrapped.warning = ((...args: Parameters<typeof sonnerToast.warning>) =>
	isMobile() ? DUMMY_ID : sonnerToast.warning(...args)) as typeof sonnerToast.warning;
wrapped.message = ((...args: Parameters<typeof sonnerToast.message>) =>
	isMobile() ? DUMMY_ID : sonnerToast.message(...args)) as typeof sonnerToast.message;
wrapped.loading = ((...args: Parameters<typeof sonnerToast.loading>) =>
	isMobile() ? DUMMY_ID : sonnerToast.loading(...args)) as typeof sonnerToast.loading;
wrapped.custom = ((...args: Parameters<typeof sonnerToast.custom>) =>
	isMobile() ? DUMMY_ID : sonnerToast.custom(...args)) as typeof sonnerToast.custom;

// promise: on mobile, run the promise silently but preserve resolution.
wrapped.promise = ((promise: Promise<unknown> | (() => Promise<unknown>), data: unknown) => {
	if (isMobile()) {
		const p = typeof promise === 'function' ? promise() : promise;
		// Swallow rejection at the toast layer (matches sonner) — caller
		// can still await the original promise themselves.
		p?.catch?.(() => {});
		return p as never;
	}
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	return (sonnerToast.promise as any)(promise, data);
}) as typeof sonnerToast.promise;

// Pass-through anything else sonner exposes that we didn't explicitly wrap.
for (const key of Object.keys(sonnerToast) as Array<keyof typeof sonnerToast>) {
	if (!(key in wrapped)) {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		(wrapped as any)[key] = (sonnerToast as any)[key];
	}
}

/** Always-fires offline notice. Use for connectivity loss. */
wrapped.offline = (message = "You're offline", data?: ExternalToast) =>
	sonnerToast.error(message, { duration: 4000, ...data });

void noop; // keep helper exported-shape stable

export const toast = wrapped;
export default toast;
